import { Button } from '@/components/ui/button'
import { MessageCircle, BookOpen, PenTool, Target, Play, Check } from 'lucide-react'
import type { LessonsCompleted, LessonKey } from '@/types/database'

// Lesson metadata mapping - includes all possible lesson types
const LESSON_METADATA: Record<LessonKey, {
  name: string
  icon: typeof MessageCircle
  description: string
  duration: string
}> = {
  warmup: {
    name: 'Let\'s Chat',
    icon: MessageCircle,
    description: 'Casual conversation about the topic',
    duration: '~3 min',
  },
  vocabulary: {
    name: 'New Words',
    icon: BookOpen,
    description: 'Learn useful vocabulary',
    duration: '~5 min',
  },
  grammar: {
    name: 'Patterns',
    icon: PenTool,
    description: 'Learn a language pattern',
    duration: '~5 min',
  },
  practice: {
    name: 'Practice',
    icon: Target,
    description: 'Apply what you learned',
    duration: '~5 min',
  },
}

// Default lesson order for A1+ (can be overridden by lessonConfig prop)
const DEFAULT_LESSON_ORDER: LessonKey[] = ['warmup', 'vocabulary', 'grammar', 'practice']

interface CurrentUnitCardProps {
  unit: {
    id: string
    task: string
    description: string | null
  }
  progress: {
    lessons_completed: LessonsCompleted
  }
  chapterProgress: {
    currentUnit: number
    totalUnits: number
    chapterNumber: number
  }
  lessonConfig?: LessonKey[]
  onStartLesson: (lessonType: string) => void
  onFreeConversation: () => void
}


// Check if lessonsCompleted is array format (new) or object format (legacy)
function isArrayFormat(lessonsCompleted: LessonsCompleted): lessonsCompleted is boolean[] {
  return Array.isArray(lessonsCompleted)
}

// Determine next lesson to start based on lessonConfig
function getNextLesson(lessonsCompleted: LessonsCompleted, lessonOrder: LessonKey[]): { lesson: LessonKey; index: number } | null {
  if (isArrayFormat(lessonsCompleted)) {
    // New format: boolean array parallel to lessonOrder
    const nextIndex = lessonsCompleted.findIndex(completed => !completed)
    if (nextIndex === -1) return null
    const lesson = lessonOrder[nextIndex]
    return lesson ? { lesson, index: nextIndex } : null
  } else {
    // Legacy format: object with lesson keys
    for (let i = 0; i < lessonOrder.length; i++) {
      const lesson = lessonOrder[i]
      if (!lessonsCompleted[lesson]) {
        return { lesson, index: i }
      }
    }
    return null
  }
}

// Check if a lesson at given index is completed
function isLessonCompleted(lessonsCompleted: LessonsCompleted, lessonOrder: LessonKey[], index: number): boolean {
  if (isArrayFormat(lessonsCompleted)) {
    return lessonsCompleted[index] ?? false
  } else {
    const lessonKey = lessonOrder[index]
    return lessonKey ? (lessonsCompleted[lessonKey] ?? false) : false
  }
}

// Lesson node component for the learning path
function LessonNode({
  lessonKey,
  isCompleted,
  isCurrent,
  isLast,
}: {
  lessonKey: LessonKey
  isCompleted: boolean
  isCurrent: boolean
  isLast: boolean
}) {
  const meta = LESSON_METADATA[lessonKey]
  if (!meta) return null
  const Icon = meta.icon

  return (
    <div className="flex flex-col items-center flex-1">
      {/* Node and connecting line */}
      <div className="flex items-center w-full">
        {/* Left connector */}
        <div className="flex-1 h-1 bg-transparent" />

        {/* Node */}
        <div
          className={`
            w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
            ${isCompleted
              ? 'bg-gradient-to-br from-amber-400 to-orange-400 text-white shadow-lg shadow-amber-200/50'
              : isCurrent
                ? 'border-4 border-amber-400 bg-amber-100 animate-pulse'
                : 'border-2 border-gray-300 bg-gray-50'
            }
          `}
        >
          {isCompleted ? (
            <Check className="w-5 h-5" strokeWidth={3} />
          ) : (
            <Icon className={`w-5 h-5 ${isCurrent ? 'text-amber-600' : 'text-gray-400'}`} />
          )}
        </div>

        {/* Right connector */}
        {!isLast && (
          <div
            className={`
              flex-1 h-1 transition-all duration-500
              ${isCompleted
                ? 'bg-gradient-to-r from-amber-400 to-orange-400'
                : isCurrent
                  ? 'bg-gradient-to-r from-amber-300 to-amber-100'
                  : 'border-t-2 border-dashed border-gray-300 bg-transparent'
              }
            `}
          />
        )}
        {isLast && <div className="flex-1" />}
      </div>

      {/* Label */}
      <span className={`
        mt-2 text-xs font-medium text-center
        ${isCompleted ? 'text-amber-700' : isCurrent ? 'text-amber-600' : 'text-gray-400'}
      `}>
        {meta.name}
      </span>

      {/* Status */}
      <span className={`
        text-[10px] text-center
        ${isCompleted ? 'text-amber-600' : isCurrent ? 'text-amber-500' : 'text-gray-400'}
      `}>
        {isCompleted ? 'Done!' : isCurrent ? 'Up next' : 'Locked'}
      </span>
    </div>
  )
}

export function CurrentUnitCard({
  unit,
  progress,
  chapterProgress,
  lessonConfig,
  onStartLesson,
  onFreeConversation,
}: CurrentUnitCardProps) {
  // Use provided lessonConfig or fall back to default
  const lessonOrder = lessonConfig ?? DEFAULT_LESSON_ORDER
  const nextLessonInfo = getNextLesson(progress.lessons_completed, lessonOrder)
  const nextLesson = nextLessonInfo?.lesson ?? null
  const nextLessonIndex = nextLessonInfo?.index ?? -1
  const progressPercent = (chapterProgress.currentUnit / chapterProgress.totalUnits) * 100

  return (
    <div className="relative">
      {/* Decorative background elements */}
      <div className="absolute -top-10 -left-10 w-40 h-40 bg-gradient-to-br from-amber-200/40 to-orange-200/40 rounded-full blur-3xl" />
      <div className="absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br from-orange-200/30 to-rose-200/30 rounded-full blur-3xl" />

      {/* Main card */}
      <div className="relative bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-3xl border border-amber-200/50 shadow-xl overflow-hidden">
        {/* Top decorative bar */}
        <div className="h-2 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />

        <div className="p-6 md:p-8">
          {/* Unit header */}
          <div className="flex items-start gap-4 mb-6">
            {/* Unit number badge */}
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-400 flex items-center justify-center shadow-lg shadow-amber-200/50 flex-shrink-0 animate-bounce-in">
              <span className="text-2xl font-bold text-white">{chapterProgress.currentUnit}</span>
            </div>

            {/* Unit info */}
            <div className="flex-1 min-w-0 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-amber-700 uppercase tracking-wide">
                  Chapter {chapterProgress.chapterNumber} · Unit {chapterProgress.currentUnit} of {chapterProgress.totalUnits}
                </span>
              </div>

              {/* Progress bar */}
              <div className="h-1.5 rounded-full bg-amber-100 mb-2 overflow-hidden">
                <div
                  className="h-full bg-gradient-to-r from-amber-400 to-orange-400 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>

              <h2 className="text-lg font-semibold text-gray-800 truncate">{unit.task}</h2>
              {unit.description && (
                <p className="text-sm text-gray-600 mt-0.5 line-clamp-2">{unit.description}</p>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-amber-200/50 mb-6" />

          {/* Learning path */}
          <div className="mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <h3 className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-4">
              Your Learning Path
            </h3>

            {/* Path visualization */}
            <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-amber-100/50">
              <div className="flex items-start">
                {lessonOrder.map((lessonKey, index) => {
                  const isCompleted = isLessonCompleted(progress.lessons_completed, lessonOrder, index)
                  const isCurrent = index === nextLessonIndex
                  const isLast = index === lessonOrder.length - 1

                  return (
                    <LessonNode
                      key={`${lessonKey}-${index}`}
                      lessonKey={lessonKey}
                      isCompleted={isCompleted}
                      isCurrent={isCurrent}
                      isLast={isLast}
                    />
                  )
                })}
              </div>
            </div>
          </div>

          {/* CTA */}
          {nextLesson && (
            <div className="animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Button
                size="lg"
                onClick={() => onStartLesson(nextLesson)}
                className="w-full h-14 text-lg font-semibold bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25 hover:shadow-xl hover:shadow-amber-500/30 transition-all duration-300 hover:scale-[1.02] rounded-xl"
              >
                <Play className="w-5 h-5 mr-2" fill="currentColor" />
                Start {LESSON_METADATA[nextLesson].name}
                <span className="ml-2 text-sm font-normal opacity-80">
                  {LESSON_METADATA[nextLesson].duration}
                </span>
              </Button>
            </div>
          )}

          {/* All complete state */}
          {!nextLesson && (
            <div className="text-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-100 text-emerald-700 mb-4">
                <Check className="w-4 h-4" />
                <span className="text-sm font-medium">Unit Complete!</span>
              </div>
              <p className="text-sm text-gray-600">
                Great job! Move on to the next unit or practice freely.
              </p>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-amber-200/50 my-6" />

          {/* Free conversation link */}
          <div className="text-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <button
              onClick={onFreeConversation}
              className="text-amber-700 hover:text-amber-800 text-sm underline-offset-2 hover:underline transition-colors"
            >
              Just have a conversation instead
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
